<template>
  <div ref="listRef"
  :class="horizontal
      ? 'flex items-center gap-1.5 overflow-x-auto h-tracklify-scroll'
      : 'max-h-48 overflow-y-auto v-tracklify-scroll'"
  >
    <div
      class="app-items-list-item flex items-center gap-1.5 rounded-chip cursor-pointer text-text"
      v-for="(item, i) in items"
      :key="i"
      :class="[
        horizontal ? 'shrink-0' : 'px-2.5 py-1.5',
        selectedIndex === i && !horizontal ? 'bg-hover' : '',
      ]"
      @mouseover="selectedIndex = i"
      @click="choose(item)"
    >
      <slot name="item" :item="item" :active="selectedIndex === i">
        <div class="truncate text-left">{{ getLabel(item) }}</div>
      </slot>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick, inject } from 'vue';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  getLabel: {
    type: Function,
    default: (item) => item.label,
  },
  horizontal: {
    type: Boolean,
    default: false,
  },
  closeOnSelect: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['select']);

// provided by Dropdown when this list is rendered inside one
const dropdownClose = inject('dropdownClose', null);

const selectedIndex = ref(0);
const listRef = ref(null);

function scrollSelectedIntoView() {
  nextTick(() => {
    const holder = listRef.value;
    if (!holder) return;

    const row = holder.querySelectorAll('.app-items-list-item')[selectedIndex.value];
    if (!row) return;

    if (props.horizontal) {
      const rowRight = row.offsetLeft + row.offsetWidth;
      if (row.offsetLeft < holder.scrollLeft) {
        holder.scrollLeft = row.offsetLeft;
      } else if (rowRight > holder.scrollLeft + holder.clientWidth) {
        holder.scrollLeft = rowRight - holder.clientWidth;
      }
      return;
    }

    row.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  });
}

watch(() => props.items, (list) => {
  if (!list.length) {
    selectedIndex.value = 0;
    return;
  }
  if (selectedIndex.value > list.length - 1) {
    selectedIndex.value = list.length - 1;
  }
  scrollSelectedIntoView();
});

function selectPrev() {
  if (selectedIndex.value > 0) {
    selectedIndex.value -= 1;
    scrollSelectedIntoView();
  }
}

function selectNext() {
  const max = props.items.length - 1;
  if (max < 0) return;
  if (selectedIndex.value < max) {
    selectedIndex.value += 1;
    scrollSelectedIntoView();
  }
}

function choose(item) {
  emit('select', item);
  if (props.closeOnSelect && dropdownClose) dropdownClose();
}

function selectByKeyboard() {
  const picked = props.items[selectedIndex.value];
  if (typeof picked === 'undefined') return;
  choose(picked);
}

function resetSelection() {
  selectedIndex.value = 0;
}

defineExpose({ selectPrev, selectNext, selectByKeyboard, resetSelection });
</script>
